(function () {
  "use strict";

  const stiboIdCache = new Map();
  const stiboIdRequests = new Map();

  class ProductCardReviewsComponent extends HTMLElement {
    constructor() {
      super();
      this._isConnected = false;
      this._needsRender = false;
      this._renderRAF = null;
      this._lastHTML = null;
      this._activeRenderKey = null;
      this._storefrontToken = undefined;
    }

    // no-op helpers removed (debugging)

    static get observedAttributes() {
      return ["product"];
    }

    get product() {
      return this._product;
    }

    set product(value) {
      this._product = value;
      const originalPriceVal = parseFloat(value?.priceInfo?.originalPrice ?? 0);
      if (this._isConnected) {
        // Always render if we have a product, regardless of price
        if (value && originalPriceVal && originalPriceVal !== 0) {
          this._scheduleRender();
        } else if (value) {
          // Still render even if no originalPrice, but with empty reviews
          this._scheduleRender();
        } else {
          // Clear content when no product
          this.innerHTML = "";
          this._lastHTML = null;
          this._needsRender = false;
        }
      } else {
        this._needsRender = !!(value && originalPriceVal && originalPriceVal !== 0);
      }
    }

    connectedCallback() {
      this._isConnected = true;
      if (this._product && this._needsRender) {
        this._scheduleRender();
        this._needsRender = false;
      } else if (this._product && this._lastHTML == null) {
        this._scheduleRender();
      }
    }

    disconnectedCallback() {
      this._isConnected = false;
      this._activeRenderKey = null;
      if (this._renderRAF) {
        cancelAnimationFrame(this._renderRAF);
        this._renderRAF = null;
      }
    }

    attributeChangedCallback(name, oldVal, newVal) {
      if (name === "product" && newVal && newVal !== oldVal) {
        try {
          const parsed = JSON.parse(newVal);
          this.product = parsed;
        } catch (e) {
          // ignore invalid JSON
        }
      }
    }

    _scheduleRender() {
      if (this._renderRAF) cancelAnimationFrame(this._renderRAF);
      this._renderRAF = requestAnimationFrame(() => {
        this._renderRAF = null;
        this._doRender().catch((error) => {
          console.error("Error rendering product reviews:", error);
        });
      });
    }

    async _doRender() {
      const renderKey = {};
      this._activeRenderKey = renderKey;

      if (!this._product) {
        this.innerHTML = "";
        this._lastHTML = null;
        return;
      }

      const safeText = (val) => (val == null ? "" : String(val));
      const url = this._product?.url || "#";
      const sku = this._fallbackSku(safeText);
      const stiboId = await this._fetchStiboProductId(this._getActiveVariantId());

      if (!this._isConnected || this._activeRenderKey !== renderKey) return;

      const baseSku = stiboId || sku;
      const reviewsHtml = baseSku
        ? `<div class="start-reviews" data-bv-show="inline_rating" data-bv-product-id="${this._escapeHtml(baseSku)}" data-bv-redirect-url="${url}" data-bv-seo="false" data-bv-lang=""></div>`
        : `<div class="start-reviews" data-bv-show="inline_rating" data-bv-product-id="" data-bv-redirect-url="${url}" data-bv-seo="false" data-bv-lang=""></div>`;

      if (reviewsHtml !== this._lastHTML) {
        this.innerHTML = reviewsHtml;
        this._lastHTML = reviewsHtml;
      }
    }

    _fallbackSku(safeText) {
      const getNumericId = (gidOrId) =>
        typeof gidOrId === "string" && gidOrId.includes("/")
          ? gidOrId.split("/").pop()
          : String(gidOrId || "");
      const variants = Array.isArray(this._product?.variants)
        ? this._product.variants
        : [];
      const selectedVariantNumericId = safeText(this._product?.variantId || "");
      const selectedVariant =
        variants.find((variant) => getNumericId(variant.id) === selectedVariantNumericId) ||
        variants[0] ||
        null;

      return safeText(selectedVariant?.sku || variants[0]?.sku || this._product?.productId || "");
    }

    _getActiveVariantId() {
      if (this._product?.variantId) return String(this._product.variantId);
      if (this._product?.variant?.id) return this._stripId(this._product.variant.id);

      const variants = Array.isArray(this._product?.variants)
        ? this._product.variants
        : [];
      if (variants[0]?.id) return this._stripId(variants[0].id);
      return "";
    }

    _stripId(id) {
      const str = String(id || "");
      return str.includes("/") ? str.split("/").pop() : str;
    }

    _toVariantGid(id) {
      const str = String(id);
      return str.startsWith("gid://") ? str : `gid://shopify/ProductVariant/${str}`;
    }

    async _fetchStiboProductId(variantId) {
      if (!variantId) return "";
      if (stiboIdCache.has(variantId)) return stiboIdCache.get(variantId);
      if (stiboIdRequests.has(variantId)) return stiboIdRequests.get(variantId);

      const request = this._requestStiboProductId(variantId);
      stiboIdRequests.set(variantId, request);
      try {
        return await request;
      } finally {
        stiboIdRequests.delete(variantId);
      }
    }

    async _requestStiboProductId(variantId) {
      const token = this._getStorefrontToken();
      if (!token) return "";

      const countryCode = window.Shopify?.country || "" || "US";
      const rawLang = document.documentElement.lang || window.Shopify?.locale || "" || "en";
      const languageCode = String(rawLang).substring(0, 2).toUpperCase();
      const query = `
        query getStiboProductId($id: ID!, $country: CountryCode, $language: LanguageCode)
        @inContext(country: $country, language: $language) {
          node(id: $id) {
            ... on ProductVariant {
              product {
                metafield(namespace: "stibo", key: "stibo_product_id") {
                  value
                }
              }
            }
          }
        }
      `;

      try {
        const response = await fetch("/api/2026-07/graphql.json", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": token,
          },
          body: JSON.stringify({
            query,
            variables: {
              id: this._toVariantGid(variantId),
              country: countryCode,
              language: languageCode,
            },
          }),
        });

        if (!response.ok) return "";

        const data = await response.json();
        if (data?.errors) return "";

        const value = data?.data?.node?.product?.metafield?.value;
        const stiboId = value == null ? "" : String(value).trim();
        stiboIdCache.set(variantId, stiboId);
        return stiboId;
      } catch (error) {
        return "";
      }
    }

    _getStorefrontToken() {
      if (this._storefrontToken !== undefined) return this._storefrontToken;
      const tokenElement = document.querySelector(".rcc-search");
      const token = tokenElement?.dataset?.token
        ? String(tokenElement.dataset.token).trim()
        : "";
      this._storefrontToken = token || null;
      return this._storefrontToken;
    }

    _escapeHtml(value) {
      return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    }
  }

  customElements.define("rcc-product-card-reviews", ProductCardReviewsComponent);
})();
